export const debounce = function <T extends Function>(cb: T, delay = 20) {
  let timeoutID = 0;
  let callable = (...args: any) => {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => cb(...args), delay);
  };
  return <T>(<any>callable);
}

export const get = (object, path, defaultValue = null) =>
  path.split('.').reduce((xs, x) => (xs && x in xs) ? xs[x] ?? defaultValue : defaultValue, object);

/**
 * Performs a deep merge of `source` into `target`.
 * Mutates `target` only but not its objects and arrays.
 *
 * @author inspired by [jhildenbiddle](https://stackoverflow.com/a/48218209).
 */
export const merge = (target, source) => {
  const isObject = (obj) => obj && typeof obj === 'object';

  if (!isObject(target) || !isObject(source)) {
    return source;
  }

  Object.keys(source).forEach(key => {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      target[key] = targetValue.concat(sourceValue);
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      target[key] = merge(Object.assign({}, targetValue), sourceValue);
    } else {
      target[key] = sourceValue;
    }
  });

  return target;
};

export const waitFor = (check: Function, maxDelay: number, initialCheck:boolean = true): Promise<number> => {
  return new Promise((resolve, reject) => {
    let currentDelay = 0;

    if(initialCheck && check()) {
      resolve(currentDelay);
      return;
    }

    let intervalId = setInterval(function() {

      if(check()) {
        clearInterval(intervalId);
        resolve(currentDelay);
        return;
      }

      if(currentDelay >= maxDelay) {
        clearInterval(intervalId);
        reject(currentDelay)
        return;
      }

      currentDelay += 50;
    }, 50);
  });
}

/**
 * Document ready for pure js
 * src: https://stackoverflow.com/questions/9899372
 * @param fn
 */
export const onDocumentReady = (fn) => {
  // see if DOM is already available
  if (document.readyState === "complete" || document.readyState === "interactive") {
    // call on next available tick
    setTimeout(fn, 1);
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
};

export const debugLog = (message: string, data: any = null, level: string = 'log') => {
  const payload = [
    message,
    data
  ].filter(value => value != null);

  switch(level) {
    case 'info':
      console.info(...payload);
      break;
    case 'warn':
      console.warn(...payload);
      break;
    case 'error':
      console.error(...payload);
      break;
    default:
      console.log(...payload);
      break;
  }
}

export const getCookie = (cookieName: string): null|string => {
  let cookie: null|string  = null;
  decodeURIComponent(window.document.cookie).split(';').find((el) => {
    let elParts = el.split('=');
    let key = elParts.shift()?.trim();
    if(key !== cookieName || elParts.length === 0) return false;

    cookie = elParts.join('=').trim();
    if(cookie === '') cookie = null;

    return true;
  })
  return cookie;
}

export const getLocalStorageJsonItem = (keyName: string, defaultValue:any = null, decode: boolean = false): any => {
  try {
    let localStorageItem = window.localStorage.getItem(keyName);
    if(!localStorageItem) return defaultValue;

    if(decode) {
      localStorageItem = atob(localStorageItem);
    }

    return JSON.parse(localStorageItem);
  }catch(error) {}

  return defaultValue;
}

export const setLocalStorageJsonItem = (keyName: string, value:any = null, encode:boolean = false): boolean => {
  try {
    let localStorageItem = JSON.stringify(value);
    if(encode) {
      localStorageItem = btoa(localStorageItem);
    }
    window.localStorage.setItem(keyName, localStorageItem);
  } catch(error) {
    return false
  }

  return true;
}

export const isJson = (val): boolean => {
  if(typeof val !== 'string') return false
  try {
    JSON.parse(val)
  } catch (e) {
    return false
  }
  return true
}

export const getJsonFromBlob = async (blob: Blob): Promise<null|Record<any, any>> => {
  return new Promise((resolve) => {
    const reader = new FileReader;
    reader.addEventListener('load', () => {
      const json = reader?.result?.toString() ?? null;
      if(!json) {
        return resolve(null);
      }
      resolve(JSON.parse(json));
    }, false);
    reader.readAsText(blob);
  });
}

export const formatShopifyPrice = (shopifyPrice: null|any, priceInCents:boolean = true) => {
  if(shopifyPrice == null) return null;

  let price = shopifyPrice;
  if(shopifyPrice.toString().indexOf(".") != -1) priceInCents = false;
  if(priceInCents) {
    price = shopifyPrice / 100
  }
  return parseFloat(price).toFixed(2)
}

// parseUri 1.2.2
// (c) Steven Levithan <stevenlevithan.com>
// MIT License
export const parseUri = (str, options?: null|Record<string, any>): Record<string, any> => {
  const o = merge({
    strictMode: false,
    key: ["source","protocol","authority","userInfo","user","password","host","port","relative","path","directory","file","query","anchor"],
    q:   {
      name:   "queryKey",
      parser: /(?:^|&)([^&=]*)=?([^&]*)/g
    },
    parser: {
      strict: /^(?:([^:\/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?))?((((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
      loose:  /^(?:(?![^:@]+:[^:@\/]*@)([^:\/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/
    }
  }, options ?? {});

  const m = o.parser[o.strictMode ? "strict" : "loose"].exec(str);
  const uri = {};
  let i = 14;

  while (i--) uri[o.key[i]] = m[i] || "";

  uri[o.q.name] = {};
  uri[o.key[12]].replace(o.q.parser, function ($0, $1, $2) {
    if ($1) uri[o.q.name][$1] = $2;
  });

  return uri;
};
